import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from 'store';
import { CentralIaState } from 'store/modules/central_ia/types';
import {
  createFolder,
  deleteChat,
  deleteFolder,
  fetchChat,
  fetchChats,
  fetchFolders,
  moveChat,
  newChat,
  resetCreateFolder,
  updateChat,
  updateFolder,
} from 'store/modules/central_ia/actions';
import StarIcon from 'assets/images/central_ia/star.svg';
import HelpVideoModal from 'components/molecules/help_video_modal';
import createTrack from 'services/actions/tracker_actions';
import { Container } from './styles';

const HELP_VIDEO_URL = 'https://player.vimeo.com/video/1209949015';

const CentralIaSidebar: React.FC = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [menuOpenFolderId, setMenuOpenFolderId] = useState<number | string | null>(null);
  const [renamingFolderId, setRenamingFolderId] = useState<number | string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [expandedFolderIds, setExpandedFolderIds] = useState<Array<number | string>>([]);
  const [menuOpenChatId, setMenuOpenChatId] = useState<number | null>(null);
  const [renamingChatId, setRenamingChatId] = useState<number | null>(null);
  const [renameChatValue, setRenameChatValue] = useState('');
  const [chatMenuMoveMode, setChatMenuMoveMode] = useState(false);
  const [draggedChatId, setDraggedChatId] = useState<number | null>(null);
  const [modalVideoOpened, setModalVideoOpened] = useState<boolean>(false);
  const [dropTargetId, setDropTargetId] = useState<number | string | 'loose' | null>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const renameChatInputRef = useRef<HTMLInputElement>(null);
  const { folders, chats, request } = useSelector<ApplicationState, CentralIaState>(
    (state) => state.session.central_ia,
  );
  const createFolderRequest = request?.create_folder || {
    loading: false,
    loaded: false,
    error: false,
  };
  const folderList = folders || [];
  const chatList = chats || [];
  const looseChats = chatList.filter((chat) => chat.central_ia_folder_id === null);
  const filteredHistory = looseChats.filter((chat) =>
    chat.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleNewChat = () => dispatch(newChat());
  const handleOpenChat = (chatId: number) => dispatch(fetchChat(chatId));

  useEffect(() => {
    if (createFolderRequest.error) dispatch(resetCreateFolder());
    dispatch(fetchFolders());
    dispatch(fetchChats());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isCreatingFolder) folderInputRef.current?.focus();
  }, [isCreatingFolder]);

  useEffect(() => {
    if (renamingFolderId !== null) renameInputRef.current?.focus();
  }, [renamingFolderId]);

  useEffect(() => {
    if (createFolderRequest.loaded) {
      setIsCreatingFolder(false);
      setFolderName('');
    }
  }, [createFolderRequest.loaded]);

  const handleStartCreatingFolder = () => {
    if (createFolderRequest.error) dispatch(resetCreateFolder());
    setFolderName('');
    setIsCreatingFolder(true);
  };

  const handleCancelFolder = () => {
    if (createFolderRequest.error) dispatch(resetCreateFolder());
    setIsCreatingFolder(false);
    setFolderName('');
  };

  const handleFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (createFolderRequest.error) dispatch(resetCreateFolder());
    setFolderName(event.target.value);
  };

  const handleSubmitFolder = () => {
    const trimmed = folderName.trim();
    if (!trimmed || createFolderRequest.loading) return;
    dispatch(createFolder(trimmed));
  };

  const handleFolderKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmitFolder();
    } else if (event.key === 'Escape') {
      handleCancelFolder();
    }
  };

  const handleFolderBlur = () => {
    if (!folderName.trim() && !createFolderRequest.loading) {
      handleCancelFolder();
    }
  };

  const handleToggleFolderMenu = (folderId: number | string) => {
    setMenuOpenFolderId((current) => (current === folderId ? null : folderId));
  };

  const handleStartRename = (folder: { id: number | string; name: string }) => {
    setMenuOpenFolderId(null);
    setRenameValue(folder.name);
    setRenamingFolderId(folder.id);
  };

  const handleCancelRename = () => {
    setRenamingFolderId(null);
    setRenameValue('');
  };

  const handleSubmitRename = (folderId: number | string) => {
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    dispatch(updateFolder(folderId, trimmed));
    setRenamingFolderId(null);
    setRenameValue('');
  };

  const handleRenameKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    folderId: number | string,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmitRename(folderId);
    } else if (event.key === 'Escape') {
      handleCancelRename();
    }
  };

  const handleDeleteFolder = (folderId: number | string) => {
    setMenuOpenFolderId(null);
    dispatch(deleteFolder(folderId));
  };

  const handleToggleExpandFolder = (folderId: number | string) => {
    setExpandedFolderIds((current) =>
      current.includes(folderId) ? current.filter((id) => id !== folderId) : [...current, folderId],
    );
  };

  useEffect(() => {
    if (renamingChatId !== null) renameChatInputRef.current?.focus();
  }, [renamingChatId]);

  const handleToggleChatMenu = (chatId: number) => {
    setChatMenuMoveMode(false);
    setMenuOpenChatId((current) => (current === chatId ? null : chatId));
  };

  const handleMoveChat = (chatId: number, folderId: number | null) => {
    setMenuOpenChatId(null);
    setChatMenuMoveMode(false);
    dispatch(moveChat(chatId, folderId));
  };

  const handleChatDragStart = (event: React.DragEvent<HTMLDivElement>, chatId: number) => {
    setDraggedChatId(chatId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(chatId));
  };

  const handleChatDragEnd = () => {
    setDraggedChatId(null);
    setDropTargetId(null);
  };

  const handleDropTargetOver = (
    event: React.DragEvent<HTMLDivElement>,
    targetId: number | string | 'loose',
  ) => {
    if (draggedChatId === null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDropTargetId(targetId);
  };

  const handleDropTargetLeave = () => {
    setDropTargetId(null);
  };

  const handleDropOnFolder = (
    event: React.DragEvent<HTMLDivElement>,
    folderId: number | string,
  ) => {
    event.preventDefault();
    if (draggedChatId !== null) {
      dispatch(moveChat(draggedChatId, Number(folderId)));
      if (!expandedFolderIds.includes(folderId)) {
        setExpandedFolderIds((current) => [...current, folderId]);
      }
    }
    setDraggedChatId(null);
    setDropTargetId(null);
  };

  const handleDropOnLoose = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (draggedChatId !== null) {
      dispatch(moveChat(draggedChatId, null));
    }
    setDraggedChatId(null);
    setDropTargetId(null);
  };

  const handleStartRenameChat = (chat: { id: number; title: string }) => {
    setMenuOpenChatId(null);
    setRenameChatValue(chat.title);
    setRenamingChatId(chat.id);
  };

  const handleCancelRenameChat = () => {
    setRenamingChatId(null);
    setRenameChatValue('');
  };

  const handleSubmitRenameChat = (chatId: number) => {
    const trimmed = renameChatValue.trim();
    if (!trimmed) {
      handleCancelRenameChat();
      return;
    }
    dispatch(updateChat(chatId, trimmed));
    setRenamingChatId(null);
    setRenameChatValue('');
  };

  const handleRenameChatKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    chatId: number,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmitRenameChat(chatId);
    } else if (event.key === 'Escape') {
      handleCancelRenameChat();
    }
  };

  const handleDeleteChat = (chatId: number) => {
    setMenuOpenChatId(null);
    dispatch(deleteChat(chatId));
  };

  const renderChatMenu = (chat: {
    id: number;
    title: string;
    central_ia_folder_id: number | null;
  }) => {
    const isChatMenuOpen = menuOpenChatId === chat.id;
    return (
      <div className="dropdown chat-actions">
        <button
          type="button"
          className="btn btn-link p-1 text-muted chat-action-btn"
          onClick={(event) => {
            event.stopPropagation();
            handleToggleChatMenu(chat.id);
          }}
          title="Opções"
        >
          <i className="material-icons">more_horiz</i>
        </button>
        <div className={`dropdown-menu dropdown-menu-right ${isChatMenuOpen ? 'show' : ''}`}>
          {chatMenuMoveMode ? (
            <>
              <button
                type="button"
                className="dropdown-item d-flex align-items-center"
                onClick={(event) => {
                  event.stopPropagation();
                  setChatMenuMoveMode(false);
                }}
              >
                <i className="material-icons mr-2">arrow_back</i>
                Mover para
              </button>
              <div className="dropdown-divider" />
              {chat.central_ia_folder_id !== null && (
                <button
                  type="button"
                  className="dropdown-item d-flex align-items-center"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleMoveChat(chat.id, null);
                  }}
                >
                  <i className="material-icons mr-2">remove_circle_outline</i>
                  Remover do projeto
                </button>
              )}
              {folderList.length === 0 ? (
                <div className="dropdown-item-text text-muted font-italic small">
                  Nenhum projeto criado
                </div>
              ) : (
                folderList
                  .filter((f) => String(f.id) !== String(chat.central_ia_folder_id))
                  .map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className="dropdown-item d-flex align-items-center"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMoveChat(chat.id, Number(f.id));
                      }}
                    >
                      <i className="material-icons mr-2">folder</i>
                      {f.name}
                    </button>
                  ))
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className="dropdown-item d-flex align-items-center"
                onClick={(event) => {
                  event.stopPropagation();
                  handleStartRenameChat(chat);
                }}
              >
                <i className="material-icons mr-2">edit</i>
                Renomear
              </button>
              <button
                type="button"
                className="dropdown-item d-flex align-items-center"
                onClick={(event) => {
                  event.stopPropagation();
                  setChatMenuMoveMode(true);
                }}
              >
                <i className="material-icons mr-2">drive_file_move</i>
                Mover
                <i className="material-icons ml-auto">chevron_right</i>
              </button>
              <button
                type="button"
                className="dropdown-item d-flex align-items-center text-danger"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteChat(chat.id);
                }}
              >
                <i className="material-icons mr-2">delete</i>
                Excluir
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderChatRenameInput = (chatId: number) => (
    <input
      ref={renameChatInputRef}
      type="text"
      className="form-control form-control-sm font-weight-light flex-grow-1 mx-2"
      value={renameChatValue}
      onChange={(event) => setRenameChatValue(event.target.value)}
      onKeyDown={(event) => handleRenameChatKeyDown(event, chatId)}
      onBlur={handleCancelRenameChat}
      onClick={(event) => event.stopPropagation()}
      maxLength={60}
    />
  );

  return (
    <>
      <Container>
        <div className="sidebar-header">
          <div className="logo-area font-weight-light">
            <img src={StarIcon} alt="Central IA" className="logo-icon" />
            Central IA
          </div>
          <button
            type="button"
            className="btn btn-link p-0 d-flex align-items-center text-nowrap help-video-button"
            onClick={() => {
              setModalVideoOpened(true);
              createTrack('Clicou no botão video tutorial', {
                fonte: 'Central IA',
              });
            }}
          >
            <i className="material-icons mr-1">videocam</i>
            Veja como funciona
          </button>
        </div>

        <div className="sidebar-section">
          <button type="button" className="sidebar-item font-weight-light" onClick={handleNewChat}>
            <i className="material-icons">edit</i>
            Novo chat
          </button>
        </div>

        <div className="search-box">
          <div className="search-input">
            <i className="material-icons">search</i>
            <input
              type="text"
              placeholder="Buscar em chats"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <i className="material-icons arrow">arrow_forward</i>
          </div>
        </div>
        <div className="sidebar-section">
          <div className="section-label font-weight-light">Projetos</div>
          {isCreatingFolder ? (
            <div className="folder-input-row d-flex align-items-center px-2 py-1">
              <i className="material-icons mr-2 text-muted">create_new_folder</i>
              <input
                ref={folderInputRef}
                type="text"
                className="form-control form-control-sm font-weight-light"
                value={folderName}
                onChange={handleFolderChange}
                onKeyDown={handleFolderKeyDown}
                onBlur={handleFolderBlur}
                placeholder="Nome do projeto"
                maxLength={60}
                disabled={createFolderRequest.loading}
              />
              {createFolderRequest.loading && (
                <div className="spinner-border spinner-border-sm text-muted ml-2" role="status">
                  <span className="sr-only">Criando...</span>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="sidebar-item font-weight-light"
              onClick={handleStartCreatingFolder}
            >
              <i className="material-icons">create_new_folder</i>
              Criar projeto
            </button>
          )}
          {createFolderRequest.error && isCreatingFolder && (
            <div className="text-danger small px-3 py-1">Erro ao criar projeto.</div>
          )}
          {folderList.map((folder) => {
            const isExpanded = expandedFolderIds.includes(folder.id);
            const folderChats = chatList.filter(
              (chat) => String(chat.central_ia_folder_id) === String(folder.id),
            );
            const isMenuOpen = menuOpenFolderId === folder.id;
            const isDropTarget = dropTargetId === folder.id;
            return (
              <div key={folder.id} className="folder-block">
                <div
                  className={`folder-row d-flex align-items-center ${
                    isMenuOpen ? 'menu-open' : ''
                  } ${isDropTarget ? 'drop-target' : ''}`}
                  onDragOver={(event) => handleDropTargetOver(event, folder.id)}
                  onDragLeave={handleDropTargetLeave}
                  onDrop={(event) => handleDropOnFolder(event, folder.id)}
                >
                  {renamingFolderId === folder.id ? (
                    <div className="d-flex align-items-center flex-grow-1 px-2 py-1">
                      <i className="material-icons mr-2 text-muted">folder</i>
                      <input
                        ref={renameInputRef}
                        type="text"
                        className="form-control form-control-sm font-weight-light"
                        value={renameValue}
                        onChange={(event) => setRenameValue(event.target.value)}
                        onKeyDown={(event) => handleRenameKeyDown(event, folder.id)}
                        onBlur={handleCancelRename}
                        maxLength={60}
                      />
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="sidebar-item font-weight-light flex-grow-1"
                        title={folder.name}
                        onClick={() => handleToggleExpandFolder(folder.id)}
                      >
                        <i className="material-icons">{isExpanded ? 'folder_open' : 'folder'}</i>
                        {folder.name}
                      </button>
                      <div className="folder-actions d-flex align-items-center">
                        <button
                          type="button"
                          className="btn btn-link p-1 text-muted folder-action-btn"
                          onClick={() => handleStartRename(folder)}
                          title="Renomear"
                        >
                          <i className="material-icons">edit</i>
                        </button>
                        <div className="dropdown">
                          <button
                            type="button"
                            className="btn btn-link p-1 text-muted folder-action-btn"
                            onClick={() => handleToggleFolderMenu(folder.id)}
                            title="Opções"
                          >
                            <i className="material-icons">more_horiz</i>
                          </button>
                          <div
                            className={`dropdown-menu dropdown-menu-right ${
                              isMenuOpen ? 'show' : ''
                            }`}
                          >
                            <button
                              type="button"
                              className="dropdown-item d-flex align-items-center"
                              onClick={() => handleStartRename(folder)}
                            >
                              <i className="material-icons mr-2">edit</i>
                              Renomear
                            </button>
                            <button
                              type="button"
                              className="dropdown-item d-flex align-items-center text-danger"
                              onClick={() => handleDeleteFolder(folder.id)}
                            >
                              <i className="material-icons mr-2">delete</i>
                              Excluir
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {isExpanded && (
                  <div className="folder-chats">
                    {folderChats.length === 0 ? (
                      <div className="folder-empty text-muted font-italic small px-3 py-2">
                        Nenhum chat neste projeto
                      </div>
                    ) : (
                      folderChats.map((chat) => {
                        const isRenamingChat = renamingChatId === chat.id;
                        const isChatMenuOpen = menuOpenChatId === chat.id;
                        const isDragging = draggedChatId === chat.id;
                        return (
                          <div
                            key={chat.id}
                            draggable={!isRenamingChat}
                            onDragStart={(event) => handleChatDragStart(event, chat.id)}
                            onDragEnd={handleChatDragEnd}
                            className={`chat-row d-flex align-items-center ${
                              isChatMenuOpen ? 'menu-open' : ''
                            } ${isDragging ? 'dragging' : ''}`}
                          >
                            {isRenamingChat ? (
                              renderChatRenameInput(chat.id)
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="sidebar-item font-weight-light folder-chat-item flex-grow-1"
                                  title={chat.title}
                                  onClick={() => handleOpenChat(chat.id)}
                                >
                                  {chat.title}
                                </button>
                                {renderChatMenu(chat)}
                              </>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div
          className={`sidebar-section history-section ${
            dropTargetId === 'loose' ? 'drop-target' : ''
          }`}
          onDragOver={(event) => handleDropTargetOver(event, 'loose')}
          onDragLeave={handleDropTargetLeave}
          onDrop={handleDropOnLoose}
        >
          <div className="section-label font-weight-light">Histórico</div>
        </div>

        <div
          className={`history-list ${dropTargetId === 'loose' ? 'drop-target' : ''}`}
          onDragOver={(event) => handleDropTargetOver(event, 'loose')}
          onDragLeave={handleDropTargetLeave}
          onDrop={handleDropOnLoose}
        >
          {filteredHistory.length === 0 ? (
            <div className="text-muted font-italic small px-3 py-2">Nenhum chat ainda</div>
          ) : (
            filteredHistory.map((chat) => {
              const isRenamingChat = renamingChatId === chat.id;
              const isChatMenuOpen = menuOpenChatId === chat.id;
              const isDragging = draggedChatId === chat.id;
              return (
                <div
                  key={chat.id}
                  draggable={!isRenamingChat}
                  onDragStart={(event) => handleChatDragStart(event, chat.id)}
                  onDragEnd={handleChatDragEnd}
                  className={`chat-row d-flex align-items-center ${
                    isChatMenuOpen ? 'menu-open' : ''
                  } ${isDragging ? 'dragging' : ''}`}
                >
                  {isRenamingChat ? (
                    renderChatRenameInput(chat.id)
                  ) : (
                    <>
                      <div
                        role="button"
                        tabIndex={0}
                        className="history-item font-weight-light flex-grow-1"
                        title={chat.title}
                        onClick={() => handleOpenChat(chat.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') handleOpenChat(chat.id);
                        }}
                      >
                        {chat.title}
                      </div>
                      {renderChatMenu(chat)}
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Container>
      <HelpVideoModal
        modalVideoOpened={modalVideoOpened}
        setModalVideoOpened={setModalVideoOpened}
        urlVideo={HELP_VIDEO_URL}
      />
    </>
  );
};

export default CentralIaSidebar;
